import type { Locale } from '@/lib/i18n'

export class ApiError extends Error {
    status: number
    fields: Record<string, string>
    code: string | null
    data: unknown

    constructor(message: string, status: number, fields: Record<string, string>, code: string | null, data: unknown) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.fields = fields
        this.code = code
        this.data = data
    }
}

interface ApiOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
    body?: unknown
    locale?: Locale
}

/** Calls this site's own API routes with the headers they require. */
export const api = async <T>(url: string, options: ApiOptions = {}): Promise<T> => {
    const locale = options.locale ?? 'ar'
    let response: Response

    try {
        response = await fetch(url, {
            method: options.method ?? 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'modulify-store',
                'X-Locale': locale
            },
            credentials: 'same-origin',
            body: options.body === undefined ? undefined : JSON.stringify(options.body)
        })
    }
    catch {
        throw new ApiError(locale === 'en' ? 'Check your internet connection and try again.' : 'تحقق من اتصالك بالإنترنت وحاول مرة أخرى.', 0, {}, 'network', null)
    }

    const data = await response.json().catch(() => null) as Record<string, unknown> | null

    if (!response.ok) {
        const message = typeof data?.error === 'string'
            ? data.error
            : locale === 'en' ? 'Something went wrong. Please try again.' : 'حدث خطأ، حاول مرة أخرى.'

        throw new ApiError(
            message,
            response.status,
            (data?.fields && typeof data.fields === 'object' ? data.fields : {}) as Record<string, string>,
            typeof data?.code === 'string' ? data.code : null,
            data
        )
    }

    return data as T
}
